<template>
<div class="st-page">
  <Navigation />
  <div class="container" style="padding-top:20px;">
    <div class="d-flex align-items-center justify-content-between mb-4">
      <div class="d-flex align-items-center gap-3">
        <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="st-back"><i class="mdi mdi-arrow-left"></i></a>
        <div><h4 class="st-page-title"><i class="mdi mdi-school me-2"></i>Classes & Grades</h4></div>
      </div>
      <button class="st-btn st-btn-blue" @click="openAdd"><i class="mdi mdi-plus me-1"></i>Add Class</button>
    </div>

    <div v-if="loading" class="st-spinner"><div class="spinner-border"></div></div>
    <div v-else>
      <div class="st-card">
        <div class="d-flex justify-content-between mb-3">
          <div class="st-search-wrap"><i class="mdi mdi-magnify"></i><input class="st-search" v-model="search" placeholder="Search classes..." /></div>
          <span style="color:#8BA4C4;font-size:13px;">{{ filteredClasses.length }} classes</span>
        </div>
        <div style="overflow-x:auto;" v-if="filteredClasses.length">
          <table class="st-table">
            <thead><tr><th>Name</th><th>Level</th><th>Stream</th><th>Class Teacher</th><th>Students</th><th>Order</th><th>Actions</th></tr></thead>
            <tbody>
              <tr v-for="c in filteredClasses" :key="c.id">
                <td style="font-weight:600;">{{ c.name }}</td>
                <td>{{ c.level }}</td>
                <td>{{ c.stream || '-' }}</td>
                <td>{{ c.teacher_name || 'Unassigned' }}</td>
                <td><span class="st-badge st-badge-info">{{ c.student_count || 0 }}</span></td>
                <td>{{ c.sort_order || 0 }}</td>
                <td>
                  <button class="st-btn st-btn-sm st-btn-blue me-1" @click="openEdit(c)" title="Edit"><i class="mdi mdi-pencil"></i></button>
                  <button class="st-btn st-btn-sm st-btn-red" @click="deleteClass(c)" title="Delete"><i class="mdi mdi-delete"></i></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="st-empty"><i class="mdi mdi-school"></i>No classes found. Add your first class to get started.</div>
      </div>
    </div>

    <!-- ADD / EDIT MODAL -->
    <div class="st-modal-overlay" v-if="showModal" @click.self="showModal=false">
      <div class="st-modal">
        <div class="st-modal-title">
          <span><i class="mdi me-2" :class="editMode?'mdi-pencil':'mdi-plus-circle'"></i>{{ editMode ? 'Edit Class' : 'Add Class' }}</span>
          <button class="st-modal-close" @click="showModal=false">&times;</button>
        </div>
        <div class="st-alert st-alert-danger" v-if="formError">{{ formError }}</div>
        <div class="st-alert st-alert-success" v-if="formSuccess">{{ formSuccess }}</div>
        <div class="mb-3">
          <label class="st-label">Class Name *</label>
          <input class="st-input" v-model="form.name" placeholder="e.g. Grade 1, Form 2 East, Year 3" />
        </div>
        <div class="row g-3 mb-3">
          <div class="col-6">
            <label class="st-label">Level *</label>
            <select class="st-select" v-model="form.level">
              <option value="">Select level...</option>
              <option value="pp1">PP1</option>
              <option value="pp2">PP2</option>
              <option value="grade1">Grade 1</option>
              <option value="grade2">Grade 2</option>
              <option value="grade3">Grade 3</option>
              <option value="grade4">Grade 4</option>
              <option value="grade5">Grade 5</option>
              <option value="grade6">Grade 6</option>
              <option value="grade7">Grade 7</option>
              <option value="grade8">Grade 8</option>
              <option value="grade9">Grade 9</option>
              <option value="form1">Form 1</option>
              <option value="form2">Form 2</option>
              <option value="form3">Form 3</option>
              <option value="form4">Form 4</option>
              <option value="year1">Year 1</option>
              <option value="year2">Year 2</option>
              <option value="year3">Year 3</option>
              <option value="year4">Year 4</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div class="col-6">
            <label class="st-label">Stream</label>
            <input class="st-input" v-model="form.stream" placeholder="e.g. East, A, Blue" />
          </div>
        </div>
        <div class="row g-3 mb-3">
          <div class="col-6">
            <label class="st-label">Class Teacher</label>
            <div class="st-teacher-picker">
              <div class="st-teacher-selected" @click="teacherDropOpen=!teacherDropOpen">
                <div v-if="selectedTeacher" class="d-flex align-items-center gap-2 flex-fill">
                  <div class="st-teacher-avatar">{{ getInitials(selectedTeacher.name) }}</div>
                  <div class="flex-fill" style="min-width:0;">
                    <div class="st-teacher-name">{{ selectedTeacher.name }}</div>
                    <div class="st-teacher-meta">{{ selectedTeacher.role_label }} · {{ selectedTeacher.phone || selectedTeacher.email || '' }}</div>
                  </div>
                </div>
                <span v-else style="color:#4A6B8A;font-size:14px;">Select teacher...</span>
                <i class="mdi" :class="teacherDropOpen?'mdi-chevron-up':'mdi-chevron-down'" style="color:#8BA4C4;flex-shrink:0;"></i>
              </div>
              <div class="st-teacher-dropdown" v-if="teacherDropOpen">
                <div class="st-teacher-search-wrap">
                  <i class="mdi mdi-magnify"></i>
                  <input class="st-teacher-search" v-model="teacherSearch" placeholder="Search by name, role, phone..." ref="teacherSearchInput" />
                </div>
                <div class="st-teacher-option" @click="selectTeacher(null)">
                  <div class="st-teacher-avatar" style="background:rgba(239,68,68,.15);color:#EF4444;">—</div>
                  <div><div class="st-teacher-name" style="color:#8BA4C4;">Unassigned</div></div>
                </div>
                <div class="st-teacher-option" v-for="t in filteredTeachers" :key="t.id" @click="selectTeacher(t)">
                  <div class="st-teacher-avatar">{{ getInitials(t.name) }}</div>
                  <div class="flex-fill" style="min-width:0;">
                    <div class="st-teacher-name">{{ t.name }}</div>
                    <div class="st-teacher-meta">
                      <span class="st-teacher-role">{{ t.role_label }}</span>
                      <span v-if="t.phone"> · {{ t.phone }}</span>
                      <span v-if="t.email"> · {{ t.email }}</span>
                    </div>
                  </div>
                </div>
                <div v-if="!filteredTeachers.length && teacherSearch" class="st-teacher-empty">No matching users found</div>
              </div>
            </div>
          </div>
          <div class="col-6">
            <label class="st-label">Sort Order</label>
            <input type="number" class="st-input" v-model="form.sortOrder" placeholder="0" min="0" />
          </div>
        </div>
        <div class="d-flex gap-2">
          <button class="btn st-btn-primary flex-fill" @click="saveClass" :disabled="processing">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Saving...' : (editMode ? 'Update Class' : 'Add Class') }}
          </button>
          <button class="btn st-btn-outline" @click="showModal=false" style="border:1px solid #1A3A6A;color:#8BA4C4;padding:12px 20px;">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/simi-auth/Navigation.vue";
import { executeJson, simitrack } from "@/api";
export default {
  name: "ClassManagement",
  components: { Navigation },
  data() {
    return {
      loading: true,
      classes: [],
      teachers: [],
      search: '',
      showModal: false,
      editMode: false,
      editId: null,
      processing: false,
      formError: '',
      formSuccess: '',
      form: { name: '', level: '', stream: '', teacherId: '', sortOrder: 0 },
      teacherDropOpen: false,
      teacherSearch: '',
      roleLabels: { super_admin: 'Super Admin', campus_manager: 'Campus Manager', class_teacher: 'Class Teacher', dining_manager: 'Dining Manager', cashier: 'Cashier', bus_manager: 'Bus Manager', bursar: 'Bursar' }
    };
  },
  computed: {
    filteredClasses() {
      if (!this.search) return this.classes;
      var s = this.search.toLowerCase();
      return this.classes.filter(function(c) {
        return (c.name || '').toLowerCase().includes(s) || (c.level || '').toLowerCase().includes(s) || (c.stream || '').toLowerCase().includes(s) || (c.teacher_name || '').toLowerCase().includes(s);
      });
    },
    selectedTeacher() {
      if (!this.form.teacherId) return null;
      return this.teachers.find(t => t.id == this.form.teacherId) || null;
    },
    filteredTeachers() {
      var self = this;
      var list = this.teachers.map(function(t) {
        return { ...t, role_label: self.roleLabels[t.role] || t.role };
      });
      if (!this.teacherSearch) return list;
      var s = this.teacherSearch.toLowerCase();
      return list.filter(function(t) {
        return (t.name || '').toLowerCase().includes(s) || (t.role || '').toLowerCase().includes(s) || (t.role_label || '').toLowerCase().includes(s) || (t.phone || '').includes(s) || (t.email || '').toLowerCase().includes(s);
      });
    }
  },
  mounted() { this.loadClasses(); this.loadTeachers(); },
  methods: {
    async loadClasses() {
      this.loading = true;
      try {
        var r = await executeJson({ function: 'getClasses' }, simitrack);
        if (r.data.success) this.classes = Array.isArray(r.data.data) ? r.data.data : (r.data.data.classes || []);
      } catch (e) { console.error(e); }
      this.loading = false;
    },

    async loadTeachers() {
      try {
        var r = await executeJson({ function: 'getUsers' }, simitrack);
        if (r.data.success) {
          var all = Array.isArray(r.data.data) ? r.data.data : (r.data.data.users || []);
          this.teachers = all.filter(function(u) { return u.is_active == 1; });
        }
      } catch (e) {}
    },

    getInitials(name) {
      if (!name) return '?';
      var parts = name.trim().split(' ');
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return name.slice(0, 2).toUpperCase();
    },

    selectTeacher(t) {
      this.form.teacherId = t ? t.id : '';
      this.teacherDropOpen = false;
      this.teacherSearch = '';
    },

    openAdd() {
      this.editMode = false;
      this.editId = null;
      this.formError = '';
      this.formSuccess = '';
      this.teacherDropOpen = false;
      this.teacherSearch = '';
      this.form = { name: '', level: '', stream: '', teacherId: '', sortOrder: 0 };
      this.showModal = true;
    },

    openEdit(c) {
      this.editMode = true;
      this.editId = c.id;
      this.formError = '';
      this.formSuccess = '';
      this.teacherDropOpen = false;
      this.teacherSearch = '';
      this.form = {
        name: c.name || '',
        level: c.level || '',
        stream: c.stream || '',
        teacherId: c.class_teacher_id || '',
        sortOrder: c.sort_order || 0
      };
      this.showModal = true;
    },

    async saveClass() {
      this.formError = '';
      this.formSuccess = '';
      if (!this.form.name || !this.form.level) { this.formError = 'Class name and level are required'; return; }
      this.processing = true;
      try {
        var params;
        if (this.editMode) {
          params = { function: 'updateClass', classId: this.editId, ...this.form };
        } else {
          params = { function: 'addClass', ...this.form };
        }
        var r = await executeJson(params, simitrack);
        if (r.data.success) {
          this.formSuccess = this.editMode ? 'Class updated!' : 'Class added!';
          await this.loadClasses();
          setTimeout(() => { this.showModal = false; this.formSuccess = ''; }, 1000);
        } else { this.formError = r.data.message || 'Failed'; }
      } catch (e) { this.formError = 'Connection error'; }
      this.processing = false;
    },

    async deleteClass(c) {
      if (c.student_count > 0) {
        alert('Cannot delete — ' + c.student_count + ' active student(s) are in this class. Move or graduate them first.');
        return;
      }
      if (!confirm('Deactivate class "' + c.name + '"? This will hide it from the list.')) return;
      try {
        var r = await executeJson({ function: 'deleteClass', classId: c.id }, simitrack);
        if (r.data.success) { this.loadClasses(); }
        else { alert(r.data.message || 'Failed'); }
      } catch (e) { alert('Connection error'); }
    }
  }
};
</script>

<style scoped>
.st-page{min-height:100vh;background:#0A1628;padding-top:70px;}
.st-back{color:#8BA4C4;font-size:22px;text-decoration:none;padding:4px;}.st-back:hover{color:#4FC3F7;}
.st-page-title{color:#E8EDF4;font-size:18px;font-weight:700;margin:0;}
.st-card{background:#0F2347;border:1px solid #1A3A6A;border-radius:14px;padding:24px;margin-bottom:16px;}
.st-table{width:100%;border-collapse:collapse;}
.st-table th{color:#8BA4C4;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:1px;padding:10px 12px;border-bottom:1px solid #1A3A6A;text-align:left;}
.st-table td{color:#E8EDF4;font-size:13px;padding:12px;border-bottom:1px solid #1A3A6A;}
.st-table tr:hover td{background:rgba(46,134,222,.05);}
.st-badge{padding:3px 10px;border-radius:50px;font-size:11px;font-weight:600;}
.st-badge-success{background:rgba(16,185,129,.15);color:#10B981;}
.st-badge-warning{background:rgba(245,158,11,.15);color:#F59E0B;}
.st-badge-danger{background:rgba(239,68,68,.15);color:#EF4444;}
.st-badge-info{background:rgba(46,134,222,.15);color:#4FC3F7;}
.st-btn{border:none;border-radius:8px;padding:8px 16px;font-size:13px;font-weight:600;cursor:pointer;transition:all .2s;}
.st-btn-sm{padding:5px 12px;font-size:12px;}
.st-btn-blue{background:rgba(46,134,222,.15);color:#4FC3F7;border:1px solid rgba(46,134,222,.3);}
.st-btn-blue:hover{background:rgba(46,134,222,.25);}
.st-btn-red{background:rgba(239,68,68,.15);color:#EF4444;border:1px solid rgba(239,68,68,.3);}
.st-btn-red:hover{background:rgba(239,68,68,.25);}
.st-btn-primary{background:linear-gradient(135deg,#2E86DE,#1A6BC4);color:#fff;border:none;font-weight:600;border-radius:10px;padding:12px;font-size:15px;}
.st-btn-primary:disabled{opacity:.6;}
.st-btn-outline{background:transparent;border-radius:10px;font-size:14px;font-weight:500;}
.st-empty{text-align:center;padding:40px 20px;color:#8BA4C4;}.st-empty i{font-size:48px;color:#1A3A6A;display:block;margin-bottom:12px;}
.st-spinner{display:flex;justify-content:center;padding:40px;}.st-spinner .spinner-border{color:#2E86DE;}
.st-search-wrap{position:relative;}.st-search-wrap i{position:absolute;left:12px;top:50%;transform:translateY(-50%);color:#8BA4C4;}
.st-search{background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:9px 14px 9px 36px;color:#E8EDF4;font-size:14px;outline:none;width:100%;max-width:300px;}
.st-modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:200;display:flex;align-items:center;justify-content:center;padding:20px;}
.st-modal{background:#0F2347;border:1px solid #1A3A6A;border-radius:16px;padding:28px;width:100%;max-width:500px;max-height:90vh;overflow-y:auto;}
.st-modal-title{color:#E8EDF4;font-size:18px;font-weight:700;margin-bottom:20px;display:flex;align-items:center;justify-content:space-between;}
.st-modal-close{color:#8BA4C4;cursor:pointer;font-size:22px;background:none;border:none;}
.st-label{color:#8BA4C4;font-size:13px;font-weight:600;margin-bottom:6px;display:block;}
.st-input,.st-select{width:100%;background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:10px 14px;color:#E8EDF4;font-size:14px;outline:none;transition:border .2s;}
.st-input:focus,.st-select:focus{border-color:#2E86DE;}
.st-alert{padding:10px 14px;border-radius:10px;font-size:13px;margin-bottom:16px;}
.st-alert-danger{background:rgba(255,107,107,.1);border:1px solid rgba(255,107,107,.2);color:#FF9999;}
.st-alert-success{background:rgba(16,185,129,.1);border:1px solid rgba(16,185,129,.2);color:#6EE7B7;}

/* Teacher Picker */
.st-teacher-picker{position:relative;}
.st-teacher-selected{background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:8px 12px;cursor:pointer;display:flex;align-items:center;justify-content:space-between;gap:8px;min-height:44px;transition:border .2s;}
.st-teacher-selected:hover{border-color:#2E86DE;}
.st-teacher-dropdown{position:absolute;top:100%;left:0;right:0;background:#0F2347;border:1px solid #1A3A6A;border-radius:10px;margin-top:4px;z-index:50;box-shadow:0 8px 30px rgba(0,0,0,.4);max-height:260px;overflow-y:auto;}
.st-teacher-search-wrap{padding:8px;border-bottom:1px solid #1A3A6A;position:relative;}
.st-teacher-search-wrap i{position:absolute;left:16px;top:50%;transform:translateY(-50%);color:#8BA4C4;font-size:16px;}
.st-teacher-search{width:100%;background:#0A1628;border:1px solid #1A3A6A;border-radius:8px;padding:7px 10px 7px 32px;color:#E8EDF4;font-size:13px;outline:none;}
.st-teacher-search:focus{border-color:#2E86DE;}
.st-teacher-option{display:flex;align-items:center;gap:10px;padding:8px 12px;cursor:pointer;transition:background .15s;}
.st-teacher-option:hover{background:rgba(46,134,222,.1);}
.st-teacher-avatar{width:32px;height:32px;border-radius:50%;background:rgba(46,134,222,.15);color:#4FC3F7;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;flex-shrink:0;}
.st-teacher-name{color:#E8EDF4;font-size:13px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.st-teacher-meta{color:#8BA4C4;font-size:11px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.st-teacher-role{background:rgba(46,134,222,.1);color:#4FC3F7;padding:1px 6px;border-radius:4px;font-size:10px;font-weight:600;}
.st-teacher-empty{padding:16px;text-align:center;color:#8BA4C4;font-size:13px;}
</style>
